import React from "react";
import {
  SEO,
  PageLayout,
  Section,
  AnimatedSelector,
  BackgroundImages,
  FishermanHeader,
  MarkdownContent,
  WebsiteComponentInteractions,
  OutboundLink,
  FadeReveal,
  InternalLink,
  ContinuousScrollV2,
  multipleComponentIterator,
  FilteredList,
  MultipleComponentIterator,
  Accordion,
  MultipleComponentIteratorMap,
  SlideReveal,
  Price,
  Image,
} from "@bluefin/components";
import { Button, Grid } from "semantic-ui-react";
import { graphql } from "gatsby";

import WebsiteLayout from "../components/WebsiteLayout.jsx";
import {
  getComponentFiles,
  getComponentContentNodeContent,
  getComponentInteractions,
  withArgs,
  withNamedArgs,
  getBusinessFiles,
} from "../utils/utils";
import {
  createServiceCategorySlug,
  createServiceItemSlug,
} from "@bluefin/gatsby-plugin-fisherman-page-creator";

export default class ServicesPage extends React.PureComponent {
  render() {
    const { data } = this.props;
    const {
      fishermanBusiness,
      fishermanBusinessWebsitePage,
      bookingUrl,
      allFishermanBusinessMenuCategory,
      allFishermanBusinessMenuSchedule,
      allFishermanBusinessMenuItem,
      allFishermanBusinessFile,
    } = data;

    return (
      <WebsiteLayout>
        <SEO
          businessName={fishermanBusiness.name}
          pageName={fishermanBusinessWebsitePage.title}
          description={fishermanBusinessWebsitePage.description}
          images={getComponentFiles({
            components: fishermanBusinessWebsitePage.components,
            componentName: "HeroElement",
            numToSelect: 1,
          })}
          title={fishermanBusinessWebsitePage.seoTitle}
          ogUrl={"https://www.ravenhairco.com/services/"}
        />
        <PageLayout hero={false} className={"services-page"}>
          <Section className={"default-hero-section"}>
            <AnimatedSelector
              targetSelector={
                ".image.background::before, .image.background::after"
              }
              duration={1500}
              transitionType={"zoom"}
              transitionProps={{
                animating: { scale: 1 },
                preAnimating: { scale: 1.4 },
                serverSide: { scale: 1.4 },
              }}
              easing={"cubic-bezier(0,1,0.99,1)"}
              runPolicy={"RUN_ONCE_EVERY_VISIT"}
              animationKey={"services-hero-animation"}
            >
              <BackgroundImages
                images={getComponentFiles({
                  components: fishermanBusinessWebsitePage.components,
                  componentName: "HeroElement",
                })}
                carouselProps={{
                  leftArrowIcon: "arrow-left",
                  rightArrowIcon: "arrow-right",
                }}
                imageProps={{ loadStrategy: "eager" }}
              >
                <div className={"content-container"}>
                  <FishermanHeader
                    as={"h1"}
                    content={getComponentContentNodeContent({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "services_hero_header",
                      defaultValue: "Our Services",
                    })}
                  />
                  <MarkdownContent
                    content={getComponentContentNodeContent({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "services_hero_description",
                    })}
                  />
                  <WebsiteComponentInteractions
                    alignment={"center"}
                    ctas={getComponentInteractions({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "services_hero_description",
                    })}
                    fallback={
                      <React.Fragment>
                        <Button
                          primary={true}
                          as={OutboundLink}
                          to={bookingUrl.url}
                          event={{
                            category: "Booking",
                            action: "Schedule Appointment Intent",
                          }}
                          content={bookingUrl.title}
                        />
                      </React.Fragment>
                    }
                  />
                </div>
              </BackgroundImages>
            </AnimatedSelector>
          </Section>
          <Section className={"about-section"}>
            <Grid stackable={true}>
              <Grid.Column width={8}>
                <FadeReveal duration={1250} triggerOnce={true} delay={0}>
                  <div>
                    <FishermanHeader
                      as={"h2"}
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "about_section_header",
                        defaultValue: "Where We Shine",
                      })}
                    />
                    <MarkdownContent
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "about_section_description",
                      })}
                    />
                    <WebsiteComponentInteractions
                      ctas={getComponentInteractions({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "about_section_description",
                      })}
                      fallback={
                        <React.Fragment>
                          <Button
                            primary={true}
                            to={"/about/"}
                            as={InternalLink}
                            content={getComponentContentNodeContent({
                              components:
                                fishermanBusinessWebsitePage.components,
                              componentId:
                                "about_section_see_more_about_button",
                              defaultValue: "About Us",
                            })}
                            event={{ category: "About", action: "View About" }}
                          />
                        </React.Fragment>
                      }
                    />
                  </div>
                </FadeReveal>
              </Grid.Column>
              <Grid.Column width={8}>
                <FadeReveal duration={1250} triggerOnce={true} delay={450}>
                  <div>
                    <FishermanHeader
                      as={"h2"}
                      className={"highlighted"}
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "about_section_highlight_header",
                        defaultValue: "Top color, blonding, and facials",
                      })}
                    />
                  </div>
                </FadeReveal>
              </Grid.Column>
            </Grid>
          </Section>
          <Section className={"digital-services-section"}>
            <ContinuousScrollV2
              multiplier={4}
              animationDurationSeconds={22}
              sizePatterns={[
                { width: "auto", height: 50, alignment: "middle" },
              ]}
              deviceSettings={{
                mobile: { elementWidth: "auto", gap: "0" },
                tablet: { elementWidth: "auto", gap: "0" },
                desktop: { elementWidth: "auto", gap: "0" },
              }}
              items={multipleComponentIterator({
                iterator: allFishermanBusinessMenuCategory.nodes,
                components: [
                  {
                    component: <ContinuousScrollV2.Item />,
                    propMap: { key: "_id" },
                    children: [
                      {
                        component: (
                          <Button
                            event={{
                              category: "Services",
                              action: "View Category Details",
                            }}
                            as={InternalLink}
                          />
                        ),
                        propMap: {
                          "event.label": "name",
                          to: createServiceCategorySlug,
                          content: "name",
                        },
                      },
                    ],
                  },
                ],
              })}
            />
            <FilteredList
              items={allFishermanBusinessMenuCategory.nodes}
              itemFilterKey={"schedule.name"}
              childItemsKey={"iterator"}
              defaultFilterValue={"All Services"}
              displayAllFilterValue={"All Services"}
              filterValues={allFishermanBusinessMenuSchedule.nodes}
              filterValueKey={"name"}
              filterValuesComponent={<Button basic={true} primary={true} />}
              className={"services-filtered-list"}
            >
              <MultipleComponentIterator
                components={[
                  {
                    component: <div className={"service-container"} />,
                    propMap: { key: "_id" },
                    children: [
                      {
                        component: <div className={"category-info"} />,
                        children: [
                          {
                            component: (
                              <FishermanHeader className={"name"} as={"h2"} />
                            ),
                            propMap: { content: "name" },
                          },
                          {
                            component: (
                              <MarkdownContent className={"description"} />
                            ),
                            propMap: { content: "description" },
                          },
                          {
                            component: (
                              <Button
                                primary={true}
                                content={getComponentContentNodeContent({
                                  components:
                                    fishermanBusinessWebsitePage.components,
                                  componentId:
                                    "see_service_category_details_button",
                                  defaultValue: "See All",
                                  enforceDefault: true,
                                })}
                                event={{
                                  category: "Services",
                                  action: "View Category Details",
                                }}
                                as={InternalLink}
                              />
                            ),
                            propMap: {
                              "event.label": "name",
                              to: createServiceCategorySlug,
                            },
                          },
                        ],
                      },
                      {
                        component: <Grid className={"category-items"} />,
                        children: [
                          {
                            component: (
                              <Grid.Column width={8} className={"items"} />
                            ),
                            children: [
                              {
                                component: <Accordion exclusive={true} />,
                                children: [
                                  {
                                    component: (
                                      <MultipleComponentIteratorMap
                                        lookupField={"_id"}
                                        limit={null}
                                        iterator={
                                          allFishermanBusinessMenuItem.nodes
                                        }
                                        components={[
                                          {
                                            component: <React.Fragment />,
                                            propMap: { key: "_id" },
                                            children: [
                                              {
                                                component: (
                                                  <Accordion.Title
                                                    activeIcon={"chevron-up"}
                                                    inactiveIcon={
                                                      "chevron-down"
                                                    }
                                                  />
                                                ),
                                                propMap: {
                                                  index: "__itemIndex__",
                                                },
                                                children: [
                                                  {
                                                    component: (
                                                      <SlideReveal
                                                        fade={true}
                                                        direction={"right"}
                                                        firstAnimation={{
                                                          direction: "up",
                                                        }}
                                                      />
                                                    ),
                                                    children: [
                                                      {
                                                        component: (
                                                          <FishermanHeader
                                                            as={"h3"}
                                                          />
                                                        ),
                                                        children: [
                                                          {
                                                            component: <span />,
                                                            propMap: {
                                                              children: "name",
                                                            },
                                                          },
                                                        ],
                                                      },
                                                    ],
                                                  },
                                                ],
                                              },
                                            ],
                                          },
                                          {
                                            component: <Accordion.Content />,
                                            propMap: { index: "__itemIndex__" },
                                            children: [
                                              {
                                                component: (
                                                  <Price
                                                    displayZeroCost={false}
                                                  />
                                                ),
                                                propMap: {
                                                  amount: "globalPriceLabel",
                                                },
                                              },
                                              {
                                                component: <MarkdownContent />,
                                                propMap: {
                                                  content: "description",
                                                },
                                              },
                                              {
                                                component: (
                                                  <Button
                                                    content={getComponentContentNodeContent(
                                                      {
                                                        components:
                                                          fishermanBusinessWebsitePage.components,
                                                        componentId:
                                                          "see_service_item_details_button",
                                                        defaultValue:
                                                          "Options & Pricing >",
                                                      }
                                                    )}
                                                    event={{
                                                      category: "Services",
                                                      action:
                                                        "View Item Details",
                                                    }}
                                                    as={InternalLink}
                                                  />
                                                ),
                                                propMap: {
                                                  "event.label": "name",
                                                  to: withArgs({
                                                    func: createServiceItemSlug,
                                                    args: [
                                                      allFishermanBusinessMenuCategory.nodes,
                                                    ],
                                                  }),
                                                },
                                              },
                                            ],
                                          },
                                        ]}
                                      />
                                    ),
                                    propMap: { targetValues: "items" },
                                  },
                                ],
                              },
                            ],
                          },
                          {
                            component: (
                              <Grid.Column width={8} className={"image"} />
                            ),
                            children: [
                              {
                                component: <Image />,
                                propMap: {
                                  src: withNamedArgs({
                                    func: getBusinessFiles,
                                    args: {
                                      businessFiles:
                                        allFishermanBusinessFile.nodes,
                                      numToSelect: 1,
                                    },
                                  }),
                                },
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ]}
              />
            </FilteredList>
          </Section>
          <Section className={"book-now-section"}>
            <Image
              background={true}
              src={getComponentFiles({
                components: fishermanBusinessWebsitePage.components,
                componentIdentifier: "book_now_section_background_image",
                numToSelect: 1,
              })}
            >
              <FishermanHeader
                as={"h2"}
                content={getComponentContentNodeContent({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_header",
                  defaultValue: "Book Your Appointment",
                })}
              />
              <MarkdownContent
                content={getComponentContentNodeContent({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_description",
                  defaultValue: "",
                })}
              />
              <WebsiteComponentInteractions
                alignment={"center"}
                ctas={getComponentInteractions({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_description",
                })}
                fallback={
                  <React.Fragment>
                    <Button
                      primary={true}
                      as={OutboundLink}
                      to={bookingUrl.url}
                      event={{
                        category: "Booking",
                        action: "Schedule Appointment Intent",
                      }}
                      content={bookingUrl.title}
                    />
                  </React.Fragment>
                }
              />
            </Image>
          </Section>
        </PageLayout>
      </WebsiteLayout>
    );
  }
}

export const query = graphql`
  query {
    fishermanBusiness {
      name
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Services" }) {
      title
      description
      components {
        fastId
        componentIdentifier
        order
        files {
          file
          altText
          gatsbyFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        contentNodes {
          content
        }
        interactions {
          _id
          order
          behaviorType
          displayType
          enabled
          label
          style
          url
          role
        }
      }
      seoTitle
    }
    bookingUrl: fishermanBusinessWebsitePage(
      title: { regex: "/Book Now|Make An Appointment/" }
      pageType: { eq: "External" }
    ) {
      url
      title
    }
    allFishermanBusinessMenuCategory(
      sort: { fields: [isFeatured, order], order: [DESC, ASC] }
    ) {
      nodes {
        name
        _id
        description
        items
        scheduleId
        schedule {
          _id
          name
        }
        files
      }
    }
    allFishermanBusinessMenuSchedule(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        order
      }
    }
    allFishermanBusinessMenuItem(
      sort: { fields: [isFeatured, order], order: [DESC, ASC] }
      filter: { visible: { eq: true } }
    ) {
      nodes {
        _id
        name
        description
        image
        visible
        available
        files
        globalPriceLabel
        variations {
          price
        }
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        description
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
  }
`;
